/**
 * The only file in this app that mentions `pdfjs-dist` — PV-5, PV-NFR-5.
 *
 * Three things are load-bearing here, and each is a decision rather than a
 * detail:
 *
 * **The import is dynamic.** `pdfjs-dist` is larger than the entire rest of
 * this bundle. Imported normally it is downloaded by every visitor, including
 * the ones who open the landing page and leave. Behind `await import()` Vite
 * emits it as its own chunk, fetched the first time somebody opens an asset
 * and never before (PV-NFR-1). Anything that imports this module statically at
 * the top level of a screen defeats that — so nothing does; the viewer calls it
 * inside an effect.
 *
 * **The worker is ours.** Every pdf.js example on the internet points
 * `workerSrc` at a CDN, and that is a third-party script with read access to
 * every document a patron opens. Vite's `?url` import emits the worker as a
 * hashed asset served from our own origin, which also means it is covered by
 * whatever CSP the site grows later rather than being the reason one cannot be
 * added (PV-NFR-5).
 *
 * **The document is built from bytes we already hold**, not from a URL pdf.js
 * fetches for itself. The bytes come from a signed S3 URL that expires; handing
 * pdf.js the URL and letting it issue range requests means an expiry mid-read
 * shows up as a half-rendered document, and it needs `Content-Range` exposed
 * through the bucket's CORS rule. Uploads are capped at 25 MB, so fetching the
 * whole thing once costs little and removes both problems (PV-6).
 */

import type { PDFDocumentProxy } from "pdfjs-dist";

/**
 * Loaded once per session, not once per document.
 *
 * `import()` caches, so this is belt-and-braces — but it also makes the worker
 * configuration happen exactly once, and setting `workerSrc` repeatedly is the
 * kind of thing that works until a version changes its mind about it.
 */
let pdfjs: typeof import("pdfjs-dist") | null = null;

async function engine() {
  if (pdfjs) return pdfjs;

  const [lib, workerUrl] = await Promise.all([
    import("pdfjs-dist"),
    // `?url` gives the emitted asset's path. `?worker` would wrap it in a
    // module worker, which is not what pdf.js wants - it constructs the
    // Worker itself from this URL.
    import("pdfjs-dist/build/pdf.worker.min.mjs?url").then((m) => m.default),
  ]);

  lib.GlobalWorkerOptions.workerSrc = workerUrl;
  pdfjs = lib;
  return lib;
}

/**
 * Fetch the bytes and open the document.
 *
 * @param url a signed S3 URL, already obtained through the entitlement gate.
 *   This function performs no authorization of its own and must never be given
 *   a URL that did not come through it.
 */
export async function loadPdf(url: string): Promise<LoadedPdf> {
  const response = await fetch(url);
  if (!response.ok) {
    // The signed URL is minted seconds earlier, so this is genuinely unusual:
    // a clock skew, a deleted object, or an expiry that has already happened.
    throw new Error(`PDF_FETCH_${response.status}`);
  }

  const bytes = await response.arrayBuffer();
  const lib = await engine();

  /*
   * The loading task, not just the document.
   *
   * pdfjs v6 moved teardown here: `PDFDocumentProxy` has `cleanup()`, which
   * frees page resources and keeps the document, while `destroy()` on the
   * *task* is what aborts outstanding work and releases the worker. Returning
   * only the proxy would leave a worker per document the reader ever opened,
   * and nothing in the viewer could reach the thing that closes it.
   */
  const task = lib.getDocument({ data: new Uint8Array(bytes) });
  const doc = await task.promise;
  return { doc, destroy: () => task.destroy() };
}

export type LoadedPdf = {
  doc: PDFDocumentProxy;
  /** Aborts outstanding work and releases the worker. */
  destroy: () => Promise<void>;
};

/** Re-exported so the viewer never imports `pdfjs-dist` for a type either. */
export type { PDFDocumentProxy, PDFPageProxy, RenderTask } from "pdfjs-dist";
